// Tap the region: which part of our own graphic each answer option stands for. The contract names the regions:
// ECG leads 'I','II','III','aVR','aVL','aVF','V1'..'V6'; observations 'rr','spo2','o2','sbp','hr','consciousness','temp'.
// Ids are matched without regard to case, and an option that stands for nothing on the graphic is handed back so the
// screen can still offer it as a plain answer.
import type { Graphic, Option, PublicItem } from '@shared/types';

export const LEAD_IDS = ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'] as const;
export type LeadId = (typeof LEAD_IDS)[number];
export const OBS_IDS = ['hr', 'spo2', 'o2', 'sbp', 'rr', 'temp', 'consciousness'] as const;
export type ObsId = (typeof OBS_IDS)[number];
export type HotspotTarget = 'ecg-lead' | 'obs-param';

/** The 12-lead read across, as it is printed (I, aVR, V1, V4 on the first row): the order of the classroom buttons. */
export const LEAD_PAPER_ORDER: readonly LeadId[] = ['I', 'aVR', 'V1', 'V4', 'II', 'aVL', 'V2', 'V5', 'III', 'aVF', 'V3', 'V6'];

export const OBS_NAME: Record<ObsId, string> = {
  hr: 'Heart rate', spo2: 'Oxygen saturation', o2: 'Oxygen', sbp: 'Blood pressure', rr: 'Respiratory rate', temp: 'Temperature', consciousness: 'Consciousness',
};
export const OBS_SHORT: Record<ObsId, string> = { hr: 'HR', spo2: 'SpO2', o2: 'O2', sbp: 'BP', rr: 'RR', temp: 'Temp', consciousness: 'ACVPU' };

export function canonicalLead(id: string): LeadId | undefined {
  const k = id.trim().toLowerCase();
  return LEAD_IDS.find((l) => l.toLowerCase() === k);
}
export function canonicalObs(id: string): ObsId | undefined {
  const k = id.trim().toLowerCase();
  return OBS_IDS.find((o) => o === k);
}

/** What the question asks players to tap: the contract's target, else what the graphic is. */
export function hotspotTarget(q: Pick<PublicItem, 'hotspot' | 'graphic'>): HotspotTarget | undefined {
  if (q.hotspot?.target === 'ecg-lead' || q.hotspot?.target === 'obs-param') return q.hotspot.target;
  if (q.graphic?.type === 'ecg') return 'ecg-lead';
  if (q.graphic?.type === 'obs') return 'obs-param';
  return undefined;
}

/** True when the graphic shows that observation (an option for a reading that is not on the monitor cannot be tapped there). */
export function obsShown(g: Graphic | undefined, id: ObsId): boolean {
  if (!g || g.type !== 'obs') return false;
  switch (id) {
    case 'hr': return typeof g.hr === 'number' && g.hr > 0;
    case 'spo2': return typeof g.spo2 === 'number';
    case 'o2': return typeof g.onO2 === 'boolean';
    case 'sbp': return typeof g.sbp === 'number';
    case 'rr': return typeof g.rr === 'number';
    case 'temp': return typeof g.temp === 'number';
    case 'consciousness': return Boolean(g.consciousness);
  }
}

export interface Region<K extends string = string> {
  /** The option id as the server sent it: the answer carries this. */
  id: string;
  /** The region it stands for on the graphic. */
  key: K;
  /** Spoken and printed name. */
  label: string;
  /** Short name for a button. */
  short: string;
}

export interface RegionMap {
  target?: HotspotTarget;
  regions: Region[];
  /** Options that stand for nothing the graphic can show. */
  extra: Option[];
}

export function regionsOf(q: Pick<PublicItem, 'hotspot' | 'graphic' | 'options'>): RegionMap {
  const target = hotspotTarget(q);
  const regions: Region[] = [];
  const extra: Option[] = [];
  const seen = new Set<string>();
  for (const o of q.options) {
    if (target === 'ecg-lead' && q.graphic?.type === 'ecg') {
      const key = canonicalLead(o.id) ?? canonicalLead(o.text);
      if (key && !seen.has(key)) { seen.add(key); regions.push({ id: o.id, key, label: `Lead ${key}`, short: key }); continue; }
    }
    if (target === 'obs-param' && q.graphic?.type === 'obs') {
      const key = canonicalObs(o.id);
      if (key && !seen.has(key) && obsShown(q.graphic, key)) {
        seen.add(key); regions.push({ id: o.id, key, label: o.text && o.text.trim() && o.text.trim().toLowerCase() !== key ? o.text : OBS_NAME[key], short: OBS_SHORT[key] }); continue;
      }
    }
    extra.push(o);
  }
  return { target, regions, extra };
}

/** "V2, V3 and V4": region names in a sentence. */
export function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
